import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { useLang } from "@/lib/lang";
import { apps, CONTACT_EMAIL } from "@/lib/apps";
import { scrollToTarget } from "@/lib/lenis";
import { ZMark } from "@/components/ZMark";
import { EASE } from "@/components/motion";

const Col = ({ title, children }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{title}</p>
    <ul className="mt-5 space-y-3 text-sm text-white/75">{children}</ul>
  </div>
);

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10 pt-20">
      <div className="container-x grid gap-12 sm:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <ZMark id="footer-z" className="h-10 w-10" />
            <span className="font-display text-2xl font-bold tracking-tight">ZenVio</span>
          </div>
          <p className="mt-5 max-w-xs text-white/55">{t.footer.tagline}</p>
          <button type="button" data-testid="footer-back-to-top" onClick={() => scrollToTarget(0)} className="btn-ghost group mt-8">
            <ArrowUp className="h-4 w-4 transition-transform duration-500 group-hover:-translate-y-1" />
            {t.footer.top}
          </button>
        </div>
        <div className="lg:col-span-2">
          <Col title={t.footer.apps}>
            {apps.map((a) => (
              <li key={a.slug}><Link to={`/apps/${a.slug}`} data-testid={`footer-app-link-${a.slug}`} className="link-underline">{a.name}</Link></li>
            ))}
          </Col>
        </div>
        <div className="lg:col-span-3">
          <Col title={t.footer.legal}>
            <li><Link to="/privacy" data-testid="footer-privacy-studio" className="link-underline">{t.footer.studioPrivacy}</Link></li>
            <li><Link to="/delete-account" data-testid="footer-delete-account" className="link-underline">{t.footer.deleteAccount}</Link></li>
            {apps.map((a) => (
              <li key={a.slug}><Link to={`/apps/${a.slug}/privacy`} data-testid={`footer-privacy-${a.slug}`} className="link-underline">{a.name} — {t.nav.privacy}</Link></li>
            ))}
          </Col>
        </div>
        <div className="lg:col-span-2">
          <Col title={t.footer.studio}>
            <li><a href={`mailto:${CONTACT_EMAIL}`} className="link-underline" data-testid="footer-email-link">{CONTACT_EMAIL}</a></li>
            <li className="text-white/55">www.zenvio.dk</li>
            <li className="text-white/55">Denmark · DK</li>
          </Col>
        </div>
      </div>
      <div className="container-x mt-16 flex flex-wrap justify-between gap-4 border-t border-white/10 py-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
        <span data-testid="footer-copyright">© {new Date().getFullYear()} ZenVio. {t.footer.rights}.</span>
        <span>iOS · Android</span>
      </div>
      <motion.div
        aria-hidden="true"
        initial={{ y: "60%" }}
        whileInView={{ y: "22%" }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: EASE }}
        className="footer-wordmark pointer-events-none select-none text-center font-display font-extrabold leading-[0.8]"
      >
        ZenVio
      </motion.div>
    </footer>
  );
};
